import { IConnection, IMessage } from '../message-queue-service';
import { PsWindow } from '../window';
import { Environment } from '../environment';
import { ServiceRegistry } from './service-registry';
import { SenderRegistry } from './sender-registry';
import { IWalletClientOptions } from '../wallet-client.options';

(window as PsWindow).paysimpleJsWallets = function (options: IWalletClientOptionsWithContainer) {
    if (!options) {
        throw new Error('No options were passed in.');
    }

    if (!options.auth) {
        throw new Error('Required property "auth" was not set.');
    }

    if (!options.container) {
        throw new Error('Required property "container" was not set.');
    }

    const walletIframeSrc = Environment.instance.hostingRoot + '/paysimplejs/v1/wallet.html';
    const targetOrigin = new URL(walletIframeSrc).origin;
    let listeners: IListenerDictionary = {};
    const services = new ServiceRegistry();
    const messageQueue = services.messageQueue;
    const senders = new SenderRegistry(services);

    let iframe = document.createElement('iframe');
    iframe.setAttribute('frameBorder', '0');
    iframe.setAttribute('scrolling', 'no');
    iframe.setAttribute('allowTransparency', 'true');
    iframe.setAttribute('allow', 'payment');
    iframe.style.width = '100%';
    iframe.style.height = '0';
    iframe.style.display = 'block';
    iframe.src = walletIframeSrc;
    iframe.onload = onload;

    options.container.style.position = 'relative';
    options.container.appendChild(iframe);

    const spinnerTarget = options.spinnerContainer || options.container;
    spinnerTarget.style.position = 'relative';
    const spinner = _createSpinner();
    spinnerTarget.appendChild(spinner);

    return {
        iframe: iframe,
        send: senders,
        destroy: destroy,
        on: on,
    };

    function onload() {
        const streamChannel = new (window as PsWindow).MessageChannel();
        const iframeContents = this.contentWindow;

        streamChannel.port1.onmessage = handleMessage;

        const message: IMessage<IWalletClientOptions> = {
            type: 'init',
            body: {
                auth: options.auth,
                digitalWalletsAcceptance: options.digitalWalletsAcceptance,
                amount: options.amount,
                enableCaptcha: options.enableCaptcha || false,
                enableCaptchaInteraction: options.enableCaptchaInteraction || false,
                recaptchaSiteKey: options.recaptchaSiteKey || undefined,
                recaptchaV2SiteKey: options.recaptchaV2SiteKey || undefined,
                recaptchaV3SiteKey: options.recaptchaV3SiteKey || undefined
            }
        };

        iframeContents.postMessage(message, targetOrigin, [streamChannel.port2]);

        function sendMessage(msg: IMessage<any>) {
            const channel = new (window as PsWindow).MessageChannel();
            iframeContents.postMessage(msg, targetOrigin, [channel.port2]);
        }

        messageQueue.setConnection(<IConnection>{ sendMessage: sendMessage });
    }

    function handleMessage(event: MessageEvent) {
        const message: IMessage<any> = event.data;

        if (!message || !message.type) {
            return;
        }

        if (message.type === 'heightChanged') {
            iframe.style.height = message.body.newValue + 'px';
        }

        if (message.type === 'walletCheckoutStarted') {
            showSpinner();
        }

        if (message.type === 'walletCancelled') {
            hideSpinner();
        }

        (listeners[message.type] || []).forEach(listener => listener.call(null, message.body));
    }

    function on(eventType: string, callback: Function) {
        (listeners[eventType] = listeners[eventType] || []).push(callback);
    }

    function destroy() {
        if (iframe && iframe.parentElement) {
            iframe.parentElement.removeChild(iframe);
        }
        iframe = null;
        listeners = {};
    }

    function showSpinner() {
        spinner.style.width = spinnerTarget.offsetWidth + 'px';
        spinner.style.height = '100%';
        spinner.style.display = 'block';
    }

    function hideSpinner() {
        spinner.style.display = 'none';
    }

    function _createSpinner(): HTMLElement {
        const style = document.createElement('style');
        style.textContent = '@keyframes psLoadingBars{0%,50%,100%{background:rgba(0,0,0,0.25)}25%{background:#000}}@-webkit-keyframes psLoadingBars{0%,50%,100%{background:rgba(0,0,0,0.25)}25%{background:#000}}';
        document.head.appendChild(style);

        const overlay = document.createElement('div');
        overlay.style.cssText = 'display:none;position:absolute;top:0;left:0;width:100%;height:100%;background:rgba(255,255,255,0.8);z-index:9999;';

        const bars = document.createElement('div');
        bars.style.cssText = 'position:absolute;top:50%;left:50%;height:50px;width:50px;margin:-25px 0 0 -25px;';

        const lefts = ['0px', '11px', '22px', '33px', '44px'];
        const delays = ['0s', '0.2s', '0.4s', '0.6s', '0.8s'];
        lefts.forEach(function (left, i) {
            const span = document.createElement('span');
            span.style.cssText = 'position:absolute;display:block;bottom:10px;width:9px;height:15px;background:rgba(0,0,0,0.25);left:' + left + ';-webkit-animation:psLoadingBars 2s infinite ease-in ' + delays[i] + ';animation:psLoadingBars 2s infinite ease-in ' + delays[i] + ';';
            bars.appendChild(span);
        });

        overlay.appendChild(bars);
        return overlay;
    }
};

interface IWalletClientOptionsWithContainer extends IWalletClientOptions {
    container: HTMLElement;
}

interface IListenerDictionary {
    [eventType: string]: Function[];
}
